import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
	isVehicleBookingWizardData,
	VehicleBookingWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/VehicleBookingWizardData';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { PassengerTypeKey } from 'Models/Enums';
import { findDriverTicket } from 'Util/_HumanWritten/DriverAssignment';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { AppConfigKeys, appConfigStore } from 'Models/AppConfigStore';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { PassengerDetailForm } from './PassengerDetailForm';
import passengerTypeStore from 'Models/PassengerTypeStore';
import { ScrollToError } from 'Validators/Functions/HumanWritten/ScrollToError';
import { assignDriverDetailsToPassenger, PassengerDetail } from '../../BookingWizardData/PassengerDetail';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { useWizardFetchedDataStore } from '../../BookingWizardState/BookingWizardFetchedData';
import { combineAllPassengerTickets, validatePassengers, PassengerError } from './PassengerTabHelpers';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';

export interface PassengerDetailsListProps {
	wizardData: BookingWizardData;
	setWizardData: (newData: BookingWizardData) => void;
	forCheckIn?: boolean;
	onPreviousPassengerSelect: (passenger: PassengerDetail, incompletePassengers: PassengerDetail[]) => void;
	onClearForm: (passenger: PassengerDetail) => void;
	resetContinue?: () => void;
	passengerDetails?: PassengerDetail[];
	userId?: string;
	/**
	 * External update path for non-wizard hosts (e.g. check-in form). When set,
	 * edits inside each passenger card flow through here instead of the wizard
	 * data store.
	 */
	onPassengerUpdate?: (updated: PassengerDetail) => void;
	/**
	 * Override for showing per-field errors. The wizard derives this from its
	 * own `innerWizardErrors`; hosts without that store can force errors on or
	 * off explicitly.
	 */
	showErrors?: boolean;
	/**
	 * Whether the booking carries a vehicle, for hosts whose booking type is not in `wizardData` — check-in
	 * keeps its own form state. Left unset, it is read from `wizardData`.
	 */
	isVehicleBooking?: boolean;
}

export function PassengerDetailsList({
	wizardData,
	setWizardData,
	forCheckIn = false,
	onPreviousPassengerSelect,
	onClearForm,
	resetContinue,
	passengerDetails,
	userId,
	onPassengerUpdate,
	showErrors: showErrorsOverride,
	isVehicleBooking: isVehicleBookingOverride,
}: PassengerDetailsListProps) {
	const { innerWizardErrors } = useBookingWizardDataStore();

	const { passengerTabData, fetchPassengerTabData } = useWizardFetchedDataStore(x => ({
		passengerTabData: x.passengerTabData,
		fetchPassengerTabData: x.fetchPassengerTabData,
	}));

	const [expandedAreas, setExpandedAreas] = useState<string[]>([]);
	const [previousPassengers, setPreviousPassengers] = useState<PassengerDetail[]>([]);
	const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

	const combinedPassengerTickets = passengerDetails ?? combineAllPassengerTickets(wizardData);
	// Passenger info is only required at check-in when the
	// `Passenger.passengerInfoInCheckIn.required` config is on. When it's off,
	// skip the validation entirely so passengers always read as "complete"
	// and no field-level errors are surfaced.
	const passengerInfoRequiredHere =
		!forCheckIn || appConfigStore.isConfigEnabled(AppConfigKeys.passengerInfoInCheckInRequired);
	// All passenger state is derived from `combinedPassengerTickets` on every render.
	// Keeping these in `useState` previously caused stale array refs that fought the
	// controlled inputs (one keystroke would echo the old value back). Computing in
	// render guarantees the UI always reflects the latest wizard data.
	const passengersErrors: PassengerError[] = passengerInfoRequiredHere
		? (validatePassengers(combinedPassengerTickets) ?? [])
		: [];
	const incompletePassengerIds = new Set(passengersErrors.map(e => e.id));
	const completedCount = combinedPassengerTickets.length - incompletePassengerIds.size;

	useEffect(() => {
		if (forCheckIn) {
			fetchPassengerTabData(userId ?? wizardData.userId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Sync driver details from the vehicle wizard data to the driver passenger
	// ticket on mount. The customer-selection flow runs `assignDriverDetailsToPassenger`
	// only when the customer is picked, which may happen before the adult ticket
	// exists. By the time the user reaches this tab the driver fields *are* set
	// on the vehicle data but the driver ticket can still be empty — so the
	// driver fields here would render as empty-and-disabled. Re-running the sync
	// covers that gap.
	useEffect(() => {
		const innerData = wizardData.innerWizardData;
		if (!isVehicleBookingWizardData(innerData)) return;
		// Every passenger type, not just adults: `CanBeDriver` is configurable, so looking only at
		// `adultTickets` meant a senior or student driver never got their details synced here and
		// the fields rendered empty-and-disabled — the gap this effect exists to close.
		const driverTicket = findDriverTicket(innerData);
		if (!driverTicket) return;
		const needsSync =
			(stringNotEmpty(innerData.driverFirstName) && stringIsEmpty(driverTicket.firstName)) ||
			(stringNotEmpty(innerData.driverLastName) && stringIsEmpty(driverTicket.lastName)) ||
			(stringNotEmpty(innerData.driverPhone) && stringIsEmpty(driverTicket.phone));
		if (needsSync) {
			const newData = new BookingWizardData({ ...wizardData });
			assignDriverDetailsToPassenger(newData);
			setWizardData(newData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Wire the driver ticket to a matching previous-passenger record so subsequent
	// edits flow into the existing person rather than creating a duplicate.
	useEffect(() => {
		if (previousPassengers.some(x => x.driver) && wizardData.tabSelected === 'vehicle') {
			const newData = new BookingWizardData({ ...wizardData });
			const currentDriverTicket = findDriverTicket(newData.innerWizardData as VehicleBookingWizardData);
			const matchingDriverTicket = previousPassengers.find(
				x => x.firstName === currentDriverTicket?.firstName && x.lastName === currentDriverTicket?.lastName,
			);
			if (currentDriverTicket && matchingDriverTicket) {
				currentDriverTicket.id = matchingDriverTicket.id;
				setWizardData(newData);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previousPassengers]);

	useEffect(() => {
		if (passengerTabData.previousPassengers) {
			setPreviousPassengers((passengerTabData.previousPassengers ?? []) as PassengerDetail[]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [passengerTabData.previousPassengers]);

	// Auto-expand the first incomplete passenger on initial mount only. We don't
	// want this to re-fire every time incomplete passengers change — that would
	// fight the user when they collapse a card to move on.
	useEffect(() => {
		if (hasAutoExpanded) return;
		const firstIncomplete = combinedPassengerTickets.find(p => incompletePassengerIds.has(p.id));
		if (firstIncomplete) {
			setExpandedAreas([firstIncomplete.id]);
			setHasAutoExpanded(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [combinedPassengerTickets.length]);

	// When the wizard reports validation errors (continue pressed with bad data),
	// open the first offending passenger and scroll to it.
	useEffect(() => {
		if (passengersErrors.length > 0 && Object.keys(innerWizardErrors).length > 0) {
			setExpandedAreas([passengersErrors[0].id]);
			ScrollToError('end');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [innerWizardErrors]);

	const toggleExpanded = (id: string, clearExpanded: boolean = true) => {
		setExpandedAreas((prev: string[]) => {
			const isExpanded = prev.includes(id);
			if (isExpanded) {
				// Only collapse when the passenger has no validation errors —
				// otherwise the user would lose visibility of what still needs fixing.
				if (!incompletePassengerIds.has(id)) {
					return prev.filter(i => i !== id);
				}
				return prev;
			}
			if (clearExpanded) {
				return [id];
			}
			return [...prev, id];
		});
	};

	const filteredPreviousPassengers = previousPassengers
		?.filter(x => !combinedPassengerTickets.map(y => y.id).includes(x.id))
		?.filter(x => {
			const incomplete = combinedPassengerTickets.filter(p => incompletePassengerIds.has(p.id));
			return incomplete.map(y => y.key).includes(x.key);
		});

	// A walk-on booking can still hold a driver flag from before a switch away from vehicle — kept so that
	// switching back restores the same driver, and stripped at submit. It is not a driver here.
	const isVehicleBooking = isVehicleBookingOverride ?? isVehicleBookingWizardData(wizardData.innerWizardData);
	const isActiveDriver = (p: PassengerDetail) => isVehicleBooking && !!p.driver;

	const incompleteNonDriverCount = combinedPassengerTickets.filter(
		p => incompletePassengerIds.has(p.id) && !isActiveDriver(p),
	).length;
	const totalCount = combinedPassengerTickets.length;
	const previousPassengerOptions = (filteredPreviousPassengers ?? []).map(p => ({
		value: p.id,
		label: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || 'Unnamed',
		passengerType: passengerTypeStore.getSingularName(p.key as PassengerTypeKey),
	}));
	// Picker is shown when the user has any previous passengers on file AND
	// still has non-driver slots to fill. If every previous passenger has
	// already been added the picker stays visible but is disabled with a
	// confirmation placeholder, so it doesn't appear and disappear underfoot.
	const hasPreviousPassengers = (previousPassengers?.length ?? 0) > 0;
	const showPreviousPassengerPicker = hasPreviousPassengers && incompleteNonDriverCount > 0;
	const noMoreToAdd = previousPassengerOptions.length === 0;

	return (
		<>
			<If condition={showPreviousPassengerPicker}>
				<SelectInput
					className="previous-passengers__combobox"
					label="Select from previous passengers"
					placeholder={noMoreToAdd ? '✓ All previous passengers added' : 'Select a previous passenger'}
					options={previousPassengerOptions}
					value={null}
					isClearable={false}
					isSearchable
					isDisabled={noMoreToAdd}
					onChange={(option: any) => {
						if (!option) return;
						const passenger = previousPassengers.find(x => x.id === option.value);
						if (passenger) {
							const incompletePassengers = combinedPassengerTickets.filter(
								p => incompletePassengerIds.has(p.id) && !isActiveDriver(p),
							);
							onPreviousPassengerSelect(passenger, incompletePassengers);
							setExpandedAreas([]);
						}
					}}
					formatOptionLabel={(option: any, meta: any) => {
						if (meta?.context === 'value') {
							return option.label;
						}
						return (
							<div className="flex flex-col">
								<span className="text-sm text-gray-900">{option.label}</span>
								{option.passengerType && (
									<span className="text-xs text-gray-500">{option.passengerType}</span>
								)}
							</div>
						);
					}}
				/>
			</If>
			<div className="passenger-details-list__container booking-section-stack">
				<section className="passenger-details-list booking-section-card">
					<header className="booking-section-header">
						<span className="booking-section-heading__icon">
							<Icon name="groups" useMaterialIcons />
						</span>
						<div className="booking-section-header__text">
							<h4 className="booking-section-title">
								<FormattedMessage
									id="passenger-tab__passenger-list-section__title"
									defaultMessage="Passengers"
								/>
							</h4>
							<p className="booking-section-description">
								<FormattedMessage
									id="passenger-tab__passenger-list-section__progress"
									defaultMessage="{completed} of {total} complete"
									values={{ completed: completedCount, total: totalCount }}
								/>
							</p>
						</div>
					</header>
					<div className="passenger-details-list__cards">
						{combinedPassengerTickets.map(x => {
							const isExpanded = expandedAreas.includes(x.id);
							const isComplete = !incompletePassengerIds.has(x.id);
							const index = combinedPassengerTickets.findIndex(y => y.id === x.id);
							return (
								<PassengerDetailForm
									key={x.id}
									count={index + 1}
									passenger={x}
									isComplete={isComplete}
									forCheckIn={forCheckIn}
									onClearForm={() => onClearForm(x)}
									expanded={isExpanded}
									setExpanded={(id, clearExpanded) => toggleExpanded(id, clearExpanded)}
									usingExistingPassenger={previousPassengers.some(y => y.id === x.id)}
									showErrors={showErrorsOverride ?? Object.keys(innerWizardErrors).length > 0}
									errors={passengersErrors}
									onPassengerUpdate={onPassengerUpdate}
									// Only the host's own answer, never the one worked out above: the form treats a
									// set value as "this host keeps its driver elsewhere" and stops locking names.
									isVehicleBooking={isVehicleBookingOverride}
									// Errors are derived from wizard data each render, so the form-level
									// setErrors call is a no-op here — left in place to preserve the API.
									setErrors={() => undefined}
									resetContinue={resetContinue}
								/>
							);
						})}
					</div>
				</section>
			</div>
		</>
	);
}
